import {
  Button,
  Filter,
  Icon,
  SearchField,
  Spinner,
  theme as defaultTheme,
} from "@mindlercare/ui-web";
import Dialog from "@mui/material/Dialog";
import { useGetPhraseLibrary } from "api/useIcbtQueries";
import type { Category, Phrase } from "api/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { dotColor } from "./categoryColor";

const MUTED = "#656565";
const BORDER = "#F0EEE9";

const snippet = (body: string): string => body.replace(/\s+/g, " ").trim();

type PhraseGroup = {
  key: string;
  label?: string;
  color?: string;
  phrases: Phrase[];
};

export type PhraseLibraryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Backend library id, e.g. "icbtassessments". */
  libraryId: string;
  /** Localized name of the field the phrase is inserted into (e.g. "Bedömning"). */
  targetFieldLabel: string;
  onInsert: (body: string) => void;
};

export const PhraseLibraryModal = ({
  isOpen,
  onClose,
  libraryId,
  targetFieldLabel,
  onInsert,
}: PhraseLibraryModalProps) => {
  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="phrase-library-title"
    >
      {isOpen && (
        <PhraseLibraryContent
          libraryId={libraryId}
          targetFieldLabel={targetFieldLabel}
          onClose={onClose}
          onInsert={onInsert}
        />
      )}
    </StyledDialog>
  );
};

const PhraseLibraryContent = ({
  libraryId,
  targetFieldLabel,
  onClose,
  onInsert,
}: Omit<PhraseLibraryModalProps, "isOpen">) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetPhraseLibrary(libraryId);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Tri-state selection: "unset" means no interaction yet, so default to the
  // first visible phrase; null means explicitly cleared (after a filter change)
  // so nothing is selected; a string is the id the user picked or navigated to.
  const [selection, setSelection] = useState<"unset" | string | null>("unset");

  const phrases = useMemo(() => data?.phrases ?? [], [data]);
  const categories = useMemo(() => data?.categories ?? [], [data]);

  const categoryByKey = useMemo(() => {
    const map = new Map<string, Category>();
    categories.forEach((c) => map.set(c.key, c));
    return map;
  }, [categories]);

  // Search filters over title + body (case-insensitive). Chip counts reflect
  // the current search, so a chip's count only tallies what search matched.
  const searchMatched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return phrases;
    return phrases.filter((p) =>
      `${p.title} ${p.body}`.toLowerCase().includes(q),
    );
  }, [phrases, search]);

  const visible = useMemo(
    () =>
      activeCategory === null
        ? searchMatched
        : searchMatched.filter((p) => p.categoryKey === activeCategory),
    [searchMatched, activeCategory],
  );

  // Group visible phrases under their category (server order preserved). Phrases
  // with no/unknown category go into a trailing headerless group (only reachable
  // via "Alla"). Phrases arrive pre-sorted, so no client sorting is needed.
  const groups = useMemo<PhraseGroup[]>(() => {
    const result: PhraseGroup[] = categories
      .map((c) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        phrases: visible.filter((p) => p.categoryKey === c.key),
      }))
      .filter((g) => g.phrases.length > 0);

    const uncategorised = visible.filter(
      (p) => !p.categoryKey || !categoryByKey.has(p.categoryKey),
    );
    if (uncategorised.length > 0) {
      result.push({ key: "__uncategorised", phrases: uncategorised });
    }
    return result;
  }, [categories, visible, categoryByKey]);

  // Resolve the tri-state selection against the visible set: unset falls back
  // to the first phrase, an explicit id must still be visible, cleared shows
  // nothing. Deriving it means a filtered-out id renders as nothing for free.
  const selectedPhrase =
    selection === "unset"
      ? (visible[0] ?? null)
      : selection === null
        ? null
        : (visible.find((p) => p.id === selection) ?? null);
  const selectedId = selectedPhrase?.id ?? null;
  const selectedCategory = selectedPhrase?.categoryKey
    ? categoryByKey.get(selectedPhrase.categoryKey)
    : undefined;

  const chips = [
    {
      key: null,
      label: t("PHRASE_LIBRARY_FILTER_ALL"),
      count: searchMatched.length,
    },
    ...categories.map((c) => ({
      key: c.key,
      label: c.label,
      count: searchMatched.filter((p) => p.categoryKey === c.key).length,
    })),
  ];

  const noResults = visible.length === 0;

  const handleInsert = () => {
    if (!selectedPhrase) return;
    onInsert(selectedPhrase.body);
    onClose();
  };

  const listRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLDivElement>(null);

  // Focus the list once content is ready so arrow keys work immediately.
  useEffect(() => {
    if (!isLoading) listRef.current?.focus();
  }, [isLoading]);

  // Changing the filter clears the selection (so ArrowDown re-enters at the
  // top). Done here, in the user-driven handlers, rather than in an effect.
  const handleSearch = (value: string) => {
    setSearch(value);
    setSelection(null);
  };
  const handleCategory = (key: string | null) => {
    setActiveCategory(key);
    setSelection(null);
  };

  // Keep the selected row in view as the selection moves via keyboard.
  useEffect(() => {
    try {
      selectedRowRef.current?.scrollIntoView({ block: "nearest" });
    } catch {
      // scrollIntoView is unavailable in some environments (e.g. jsdom)
    }
  }, [selectedId]);

  const moveSelection = (delta: number) => {
    if (visible.length === 0) return;
    const currentIndex = selectedId
      ? visible.findIndex((p) => p.id === selectedId)
      : -1;
    const nextIndex =
      currentIndex < 0
        ? delta > 0
          ? 0
          : visible.length - 1
        : Math.min(visible.length - 1, Math.max(0, currentIndex + delta));
    setSelection(visible[nextIndex].id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveSelection(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveSelection(-1);
        break;
      case "Home":
        e.preventDefault();
        if (visible[0]) setSelection(visible[0].id);
        break;
      case "End":
        e.preventDefault();
        if (visible.length) setSelection(visible[visible.length - 1].id);
        break;
      case "Enter":
        // Let focused buttons (footer, chips, close) handle their own Enter.
        if (!(e.target as HTMLElement).closest("button")) {
          e.preventDefault();
          handleInsert();
        }
        break;
    }
  };

  return (
    <Root onKeyDown={handleKeyDown}>
      <Header>
        <HeaderText>
          <Title id="phrase-library-title">{t("PHRASE_LIBRARY_TITLE")}</Title>
          <Subtitle>
            <Icon.Template width={20} height={20} />
            <span>
              {t("PHRASE_LIBRARY_SUBTITLE", { field: targetFieldLabel })}
            </span>
          </Subtitle>
        </HeaderText>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={t("PHRASE_LIBRARY_CANCEL")}
        >
          <Icon.Close width={20} height={20} />
        </CloseButton>
      </Header>

      {isLoading ? (
        <Centered>
          <Spinner size={40} />
        </Centered>
      ) : (
        <>
          <Controls>
            <SearchField
              onChange={handleSearch}
              placeholder={t("PHRASE_LIBRARY_SEARCH_PLACEHOLDER")}
              sx={{ width: "100%" }}
            />
            {categories.length > 0 && (
              <Chips>
                {chips.map((chip) => (
                  <Filter
                    key={chip.key ?? "__all"}
                    isOn={activeCategory === chip.key}
                    onClick={() => handleCategory(chip.key)}
                  >
                    {`${chip.label} · ${chip.count}`}
                  </Filter>
                ))}
              </Chips>
            )}
          </Controls>

          <Body>
            <ListPane
              ref={listRef}
              role="listbox"
              tabIndex={0}
              aria-label={t("PHRASE_LIBRARY_TITLE")}
              aria-activedescendant={
                selectedPhrase ? `phrase-opt-${selectedPhrase.id}` : undefined
              }
            >
              {noResults ? (
                <ListEmpty>{t("PHRASE_LIBRARY_NO_MATCHES")}</ListEmpty>
              ) : (
                groups.map((group) => (
                  <div key={group.key}>
                    {group.label && (
                      <CategoryLabel $inList>
                        <Dot $color={dotColor(group.color)} />
                        <span>{group.label}</span>
                      </CategoryLabel>
                    )}
                    {group.phrases.map((phrase) => (
                      <Row
                        key={phrase.id}
                        role="option"
                        id={`phrase-opt-${phrase.id}`}
                        aria-selected={phrase.id === selectedPhrase?.id}
                        ref={
                          phrase.id === selectedPhrase?.id
                            ? selectedRowRef
                            : undefined
                        }
                        data-testid={`phrase-row-${phrase.id}`}
                        $selected={phrase.id === selectedPhrase?.id}
                        onClick={() => {
                          setSelection(phrase.id);
                          // Keep focus on the listbox so navigation continues
                          // from here without a stray focus ring on the row.
                          listRef.current?.focus();
                        }}
                      >
                        <RowTitle>
                          <Dot $color={dotColor(group.color)} />
                          <span>{phrase.title}</span>
                        </RowTitle>
                        <RowPreview>{snippet(phrase.body)}</RowPreview>
                      </Row>
                    ))}
                  </div>
                ))
              )}
            </ListPane>

            <PreviewPane>
              {selectedPhrase ? (
                <>
                  <PreviewHeader>
                    {selectedCategory && (
                      <CategoryLabel>
                        <Dot $color={dotColor(selectedCategory.color)} />
                        <span>{selectedCategory.label}</span>
                      </CategoryLabel>
                    )}
                    <PreviewTitle>{selectedPhrase.title}</PreviewTitle>
                  </PreviewHeader>
                  <PreviewBody>{selectedPhrase.body}</PreviewBody>
                  <Footer>
                    <Button
                      variant="secondary"
                      onClick={onClose}
                      testId="phrase-library-cancel"
                    >
                      {t("PHRASE_LIBRARY_CANCEL")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleInsert}
                      testId="phrase-library-insert"
                    >
                      {t("PHRASE_LIBRARY_INSERT", { field: targetFieldLabel })}
                    </Button>
                  </Footer>
                </>
              ) : (
                <PreviewEmpty>
                  <Icon.Search width={32} height={32} />
                  <span>{t("PHRASE_LIBRARY_PREVIEW_EMPTY")}</span>
                </PreviewEmpty>
              )}
            </PreviewPane>
          </Body>
        </>
      )}
    </Root>
  );
};

const StyledDialog = styled(Dialog)`
  /* Sit above the quick-picker's Select panel (z-index 1500) and any parent
     modal so the phrase library never opens behind them. */
  &.MuiDialog-root {
    z-index: 1600;
  }

  & .MuiDialog-paper {
    width: 780px;
    max-width: 780px;
    height: 600px;
    max-height: calc(100vh - 64px);
    border-radius: 0;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(29, 27, 27, 0.28);
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  font-family: ${defaultTheme.FONT.FAMILY.BODY};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

// Inter Display Medium, 24 (design-system title spec).
const Title = styled.h2`
  margin: 0;
  font-family: ${defaultTheme.FONT.FAMILY.HEADERS};
  font-size: ${defaultTheme.FONT.SIZE.EXTRA_LARGE};
  font-weight: ${defaultTheme.FONT.WEIGHT.MEDIUM};
  line-height: 1.2;
  color: ${defaultTheme.COLORS.GRAY_BLACK};
`;

// Icon row: 20px gray icon, Body 16 / dark-gray text.
const Subtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${defaultTheme.COLORS.GRAY_DARK};
  font-size: ${defaultTheme.FONT.SIZE.BODY};

  & svg {
    color: ${defaultTheme.COLORS.GRAY};
  }
`;

const CloseButton = styled.button`
  border: 0;
  background: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  color: ${MUTED};
  display: flex;

  &:hover {
    background: ${defaultTheme.COLORS.GRAY_LIGHT};
  }
`;

const Controls = styled.div`
  padding: 0 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  border-top: 1px solid ${BORDER};
`;

const ListPane = styled.div`
  width: 300px;
  flex: none;
  overflow-y: auto;
  border-right: 1px solid ${BORDER};
  padding: 6px 10px 14px;

  &:focus {
    outline: none;
  }
`;

// Shared category-label styling so the list section headers and the preview
// header render identically. `$inList` adds the list's inter-group spacing.
const CategoryLabel = styled.div<{ $inList?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 10.5px;
  font-weight: ${defaultTheme.FONT.WEIGHT.BOLD};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${defaultTheme.COLORS.GRAY};
  ${({ $inList }) =>
    $inList ? "padding: 14px 8px 5px;" : "margin-bottom: 8px;"}
`;

const Row = styled.div<{ $selected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: 9px 8px;
  border-radius: 9px;
  margin-top: 2px;
  background: ${({ $selected }) =>
    $selected ? defaultTheme.COLORS.PRIMARY_LIGHT : "transparent"};

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? defaultTheme.COLORS.PRIMARY_LIGHT
        : defaultTheme.COLORS.GRAY_LIGHT};
  }
`;

const RowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${defaultTheme.FONT.WEIGHT.MEDIUM};
  line-height: 1.3;
  color: ${defaultTheme.COLORS.GRAY_BLACK};
`;

const RowPreview = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${MUTED};
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Dot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex: none;
`;

const ListEmpty = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${defaultTheme.COLORS.GRAY};
`;

const PreviewPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PreviewHeader = styled.div`
  padding: 20px 24px 12px;
  border-bottom: 1px solid ${BORDER};
`;

const PreviewTitle = styled.div`
  font-family: ${defaultTheme.FONT.FAMILY.HEADERS};
  font-size: 17px;
  font-weight: ${defaultTheme.FONT.WEIGHT.SEMIBOLD};
  line-height: 1.25;
  color: ${defaultTheme.COLORS.GRAY_BLACK};
`;

const PreviewBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  font-size: 14px;
  line-height: 1.65;
  color: ${defaultTheme.COLORS.GRAY_DARK};
  white-space: pre-wrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid ${BORDER};
`;

const PreviewEmpty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
  font-size: 13px;
  line-height: 1.4;
  color: ${defaultTheme.COLORS.GRAY};
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
